import screenfull from 'screenfull';
import { detectIncognito } from 'detectincognitojs';
import { BettingOptionsFormat } from '@betgames/bg-tools';
import { envConfig } from '@bg-shared/constants/envConfig';
import { GameId } from '@bg-shared/enums/GameId';
import { BetTypes } from '@bg-shared/business/Analytics/enums/BetTypes';
import { IBatchResponse, IBatchResponseLegacy } from '@bg-shared/business/Betting/interfaces';
import { multiTypeCheck } from '@bg-shared/business/Betting/utils';
import { playerSettingsStore } from '@bg-shared/business/PlayerSettings';
import { EventTypes } from '@bg-shared/integrations/shared/EventTypes';
import { IWindowData } from '@bg-shared/integrations/shared/interfaces';
import { abTesting } from './ABTesting';

const stringifyEvent = (data: unknown): string => JSON.stringify(data).replace(/"/g, '~');

export default class AnalyticsApi {
    private SITE_URL = 'https://services.launcher-7tgld3-int.com';

    private SITE_ID = _globalConfig.matomoSiteId;

    private currentURL = `${window.location.pathname}${window.location.search}`;

    private userID: number;

    /*
        0.  'trackEvent'
        1.  Event Category
        2.  Event Action
        3.  Event Name
        4.  Event Value
     */
    private pushEvent(args: (string | number | Record<string, unknown>)[]): void {
        if (!window._paq) {
            window._paq = [];
        }

        window._paq.push(args);
    }

    private trackEvent(args: (string | number | Record<string, unknown>)[]): void {
        this.pushEvent(['trackEvent', ...args]);
    }

    private trackFrontEvent(args: (string | number | Record<string, unknown>)[]): void {
        this.trackEvent(['Frontend', ...args]);
    }

    private trackUserID(): void {
        this.pushEvent(['setUserId', this.userID]);
    }

    private setupABTests() {
        abTesting.tests.forEach((test) => {
            this.pushEvent([
                'AbTesting::create',
                {
                    name: test.name,
                    percentage: test.percentage,
                    trigger: () => (typeof test.trigger !== 'undefined' ? test.trigger : true),
                    variations: test.variations.map((variation) => ({
                        name: variation,
                        activate: () => {
                            console.info(`AB test [${test.name}] activated: `, variation);
                        },
                    })),
                },
            ]);
        });
    }

    private loadScript(): void {
        const script = document.createElement('script');
        script.async = true;
        script.src = `${this.SITE_URL}/main.js`;
        document.body.appendChild(script);
    }

    private trackPrivateBrowsing(): void {
        detectIncognito()
            .then((result) => {
                if (result.isPrivate) {
                    this.trackFrontEvent(['privateBrowsing', result.browserName]);
                }
            })
            .catch(() => {});
    }

    private trackBettingOption(gameId: GameId, placement: string): void {
        if (_featuresConfig.trackGameBettingOptions?.includes(gameId)) {
            this.trackFrontEvent(['bettingOptionSelection', stringifyEvent({ gameId, placement })]);
        }
    }

    private trackCasinoAction(gameId: GameId, action: string): void {
        if (_featuresConfig.trackGameCasinoActions?.includes(gameId)) {
            this.trackFrontEvent([action, gameId]);
        }
    }

    private trackPlaceBet(
        betID: number | string,
        type: string,
        isRandom: boolean,
        isRepeat?: boolean,
        isAutoplay = false,
    ): void {
        this.trackEvent([
            'betting',
            'placebet',
            stringifyEvent({
                guiMode: Number(playerSettingsStore.guiMode),
                isAutoplay: Number(isAutoplay),
            }),
            betID,
            {
                dimension1: betID,
                dimension5: !!screenfull.isFullscreen,
                dimension7: type,
                dimension6: !!isRandom,
                dimension10: !!isRepeat,
            },
        ]);
    }

    public enableTracking(options: {
        partnerCode: string;
        userID: number;
        locale: string;
        isNewIntegration?: boolean;
        scriptIntegration?: boolean;
        onlyGUI?: boolean;
        defaultGUI?: boolean;
        defaultGame?: number;
    }): void {
        if (!this.SITE_ID) {
            return;
        }

        this.userID = options.userID;

        this.trackPageView();

        this.pushEvent(['enableLinkTracking']);
        this.pushEvent(['enableHeartBeatTimer']);
        this.pushEvent(['setCustomDimension', 3, options.locale]);
        this.pushEvent(['setReferrerUrl', document.referrer]);
        this.pushEvent(['setCookieSameSite', 'None']);
        this.pushEvent(['setTrackerUrl', `${this.SITE_URL}/main.php`]);
        this.pushEvent(['setSiteId', this.SITE_ID]);

        this.trackUserID();
        this.setupABTests();

        this.trackFrontEvent([
            'initParams',
            stringifyEvent({
                partnerCode: options.partnerCode,
                webVersionBuild: envConfig.version,
                isNewIntegration: Number(options.isNewIntegration),
                scriptIntegration: Number(options.scriptIntegration),
                ...(typeof options.onlyGUI !== 'undefined' && {
                    onlyGUI: Number(options.onlyGUI),
                }),
                ...(typeof options.defaultGUI !== 'undefined' && {
                    defaultGUI: Number(options.defaultGUI),
                }),
                ...(typeof options.defaultGame !== 'undefined' && {
                    defaultGame: options.defaultGame,
                }),
                isFullscreenAllowed: Number(screenfull.isEnabled),
            }),
        ]);
        this.trackPrivateBrowsing();
        this.loadScript();
    }

    public trackPageView(): void {
        const newUrl = `${window.location.pathname}${window.location.search}`;

        if (newUrl === this.currentURL) {
            return;
        }

        this.trackUserID();
        this.pushEvent(['enableHeartBeatTimer']);

        if (this.currentURL) {
            this.pushEvent(['setReferrerUrl', this.currentURL]);
        }

        this.currentURL = newUrl;
        this.pushEvent(['setCustomUrl', newUrl]);
        this.pushEvent(['trackPageView']);
    }

    public trackFullScreenSwitch(value: boolean): void {
        this.trackFrontEvent(['fullScreenSwitch', value ? 'on' : 'off']);
    }

    public trackSoundSwitch(muted: boolean): void {
        this.trackFrontEvent(['soundSwitch', muted ? 'off' : 'on']);
    }

    public trackStudioSoundSettingSwitch(value: boolean): void {
        this.trackFrontEvent(['studioSoundSettingSwitch', value ? 'on' : 'off']);
    }

    public trackGameEffectsSettingSwitch(value: boolean): void {
        this.trackFrontEvent(['gameEffectsSettingSwitch', value ? 'on' : 'off']);
    }

    public trackVideoSettingSwitch(value: boolean): void {
        this.trackFrontEvent(['videoSettingSwitch', value ? 'on' : 'off']);
    }

    public trackVideoQualitySwitch(gameId: GameId, quality: string, placement: string): void {
        this.trackFrontEvent([
            'videoQualitySwitch',
            stringifyEvent({ gameId, quality, placement }),
        ]);
    }

    public trackGuiSwitch(value: boolean, placement: string): void {
        this.trackFrontEvent([
            'guiModeSwitch',
            stringifyEvent({
                value: value ? 'on' : 'off',
                placement,
            }),
        ]);
    }

    public trackCCTVSwitch(value: boolean): void {
        this.trackFrontEvent(['cctvSwitch', value ? 'on' : 'off']);
    }

    public trackGuiModeEnterFromTip(): void {
        this.trackFrontEvent(['enterGuiModeFromTip']);
    }

    public trackCasinoSwitch(value: boolean): void {
        this.trackFrontEvent(['casinoModeSwitch', value ? 'on' : 'off']);
    }

    public trackOddsFormatSwitch(format: BettingOptionsFormat): void {
        this.trackFrontEvent(['oddsFormatSwitch', format]);
    }

    public trackInactivePlaceBetButton(): void {
        this.trackFrontEvent(['inactivePlaceBetButton']);
    }

    public trackBaccaratStatsSwitchOn(): void {
        this.trackFrontEvent(['baccaratStats']);
    }

    public trackPoker6PlusRankingSwitchOn(): void {
        this.trackFrontEvent(['poker6PlusRankings']);
    }

    public trackOpenGameFromLobby(): void {
        if (_featuresConfig.trackGamesNavigation) {
            this.trackFrontEvent(['openGameFromLobbyNavigation']);
        }
    }

    public trackOpenGameFromOtherGames(): void {
        if (_featuresConfig.trackGamesNavigation) {
            this.trackFrontEvent(['openGameFromOtherGamesNavigation']);
        }
    }

    public trackQuickPickButton(gameId: GameId): void {
        this.trackFrontEvent(['quickPickButton', gameId]);
    }

    public trackQuickPickLotteryItems(gameId: GameId): void {
        this.trackFrontEvent(['quickPickLotteryItems', gameId]);
    }

    public trackRepeatBetModal(): void {
        this.trackFrontEvent(['repeatBetModal']);
    }

    public trackRepeatAll(): void {
        this.trackFrontEvent(['repeatAll']);
    }

    public trackAmountButton(amount: number, gameId: GameId): void {
        if (_featuresConfig.trackGameAmountsButtons?.includes(gameId)) {
            this.trackFrontEvent(['amountButton', stringifyEvent({ gameId, amount })]);
        }
    }

    public trackClearAmountButton(): void {
        if (_featuresConfig.trackClearAmountButton) {
            this.trackFrontEvent(['clearAmountButton']);
        }
    }

    public trackMaxAmountButton(): void {
        if (_featuresConfig.trackMaxAmountButton) {
            this.trackFrontEvent(['maxAmountButton']);
        }
    }

    public trackCasinoUndo(gameId: GameId): void {
        this.trackCasinoAction(gameId, 'casinoUndo');
    }

    public trackCasinoRepeat(gameId: GameId): void {
        this.trackCasinoAction(gameId, 'casinoRepeat');
    }

    public trackCasinoDouble(gameId: GameId): void {
        this.trackCasinoAction(gameId, 'casinoDouble');
    }

    public trackCasinoMove(gameId: GameId): void {
        this.trackCasinoAction(gameId, 'casinoMove');
    }

    public trackCashOutError(gameId: GameId, data: Record<string, unknown>): void {
        this.trackCasinoAction(gameId, 'casinoMove');
        this.trackFrontEvent(['cashOutError', stringifyEvent(data)]);
    }

    public trackCasinoPlaceBet(betIDs: number[], isAutoplay = false): void {
        this.trackEvent([
            'betting',
            'placebetCasino',
            stringifyEvent({
                guiMode: Number(playerSettingsStore.guiMode),
                bets: betIDs,
                isAutoplay: Number(isAutoplay),
            }),
            betIDs.length,
            {
                dimension5: !!screenfull.isFullscreen,
            },
        ]);
    }

    public trackRNGBet(options: {
        gameId: GameId;
        hash: string;
        isAutoplay?: number;
        isRespin?: number;
    }): void {
        this.trackEvent(['betting', 'placebet-rng', stringifyEvent(options)]);
    }

    public trackPlaceBetSingle(
        betId: number | string,
        random: boolean,
        repeat = false,
        isAutoplay = false,
    ): void {
        this.trackPlaceBet(betId, BetTypes.SINGLE, random, repeat, isAutoplay);
    }

    public trackPlaceBetSubscription(betId: number, random: boolean, repeat = false): void {
        this.trackPlaceBet(betId, BetTypes.SUBSCRIPTION, random, repeat);
    }

    public trackPlaceBetCombo(betId: number, random: boolean): void {
        this.trackPlaceBet(betId, BetTypes.COMBINATION, random);
    }

    public trackPlaceBetMulti(
        response: IBatchResponseLegacy | IBatchResponse,
        repeat = false,
    ): void {
        response.forEach((item) => {
            if (multiTypeCheck.isLegacySingle(item)) {
                this.trackPlaceBetSingle(item.data.bet.id, false, repeat);
            } else if (multiTypeCheck.isLegacySubscription(item)) {
                this.trackPlaceBetSubscription(item.data.subscription.id, false, repeat);
            } else if (multiTypeCheck.isSingle(item)) {
                this.trackPlaceBetSingle(item.bet.id, false, repeat);
            } else if (multiTypeCheck.isSubscription(item)) {
                this.trackPlaceBetSubscription(item.subscription.id, false, repeat);
            }
        });
    }

    public trackInvalidBets(error: unknown | Error): void {
        const message =
            typeof error === 'string'
                ? error
                : (error as { error: string })?.error || (error as Error)?.message;

        this.trackFrontEvent(['invalidbet', message]);
    }

    public trackBettingOptionFromList(gameId: GameId): void {
        this.trackBettingOption(gameId, 'mainList');
    }

    public trackBettingOptionFromVideoTable(gameId: GameId): void {
        this.trackBettingOption(gameId, 'videoTable');
    }

    public trackBettingOptionFromVideoPanel(gameId: GameId): void {
        this.trackBettingOption(gameId, 'videoPanel');
    }

    public trackCloseAutoRouletteStatistics(gameId: GameId): void {
        this.trackFrontEvent(['closeAutoRouletteStatistics', gameId]);
    }

    public trackDealerLobbyButton(gameId: GameId): void {
        this.trackFrontEvent(['dealerLobbyButton', gameId]);
    }

    public trackDealersLanguageChange(gameId: GameId): void {
        this.trackFrontEvent(['dealersLanguageChange', gameId]);
    }

    public trackRouletteTableSwitch(gameId: GameId, isFrench?: boolean): void {
        this.trackFrontEvent([
            'rouletteTable',
            stringifyEvent({ gameId, table: isFrench ? 'french' : 'classic' }),
        ]);
    }

    public trackFrenchBetNeighboursCount(gameId: GameId, neighbours: number): void {
        this.trackFrontEvent(['frenchBetNeighboursCount', stringifyEvent({ gameId, neighbours })]);
    }

    public trackStatisticsOpen(gameId: GameId): void {
        if (_featuresConfig.trackStatistics) {
            this.trackFrontEvent(['statisticsOpen', gameId]);
        }
    }

    public trackVideoQualityAutoAdjust(on: boolean): void {
        this.trackFrontEvent(['videoQualityAutoAdjust', on ? 'on' : 'off']);
    }

    public trackGameSoundSettingAdjust(): void {
        this.trackFrontEvent(['gameSoundSettingAdjust']);
    }

    public trackGameSettingsOpened(opened: boolean): void {
        this.trackFrontEvent(['gameSettingsOpened', opened ? 'opened' : 'closed']);
    }

    public trackPressedRepeat(): void {
        this.trackFrontEvent(['pressedRepeat']);
    }

    public trackVideoQualitySettingsChose(): void {
        this.trackFrontEvent(['videoQualitySettingsChose']);
    }

    public trackScreenNameEnter(): void {
        this.trackFrontEvent(['screenNameEnter']);
    }

    public trackScreenNameChange(): void {
        this.trackFrontEvent(['screenNameChange']);
    }

    public trackPressedDealNow(): void {
        this.trackFrontEvent(['pressedDealNow']);
    }

    public trackMadePredecision(): void {
        this.trackFrontEvent(['madePredecision']);
    }

    public trackIframeIntegrationFallback(
        partnerCode: string,
        fallbackEventType: EventTypes | string,
        windowData?: IWindowData,
    ): void {
        const trackEventData = ['iframeIntegrationFallback', partnerCode];

        if (windowData) {
            trackEventData.push(stringifyEvent(fallbackEventType));
        }

        this.trackFrontEvent(trackEventData);
    }
}

export const analyticsApi = new AnalyticsApi();
